import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerUserResponseAnswer,
  CareerQuestion,
  CareerUserResponseSet,
  SchoolLevel,
  SCHOOL_LEVEL,
} from '@backrs_ui/grow-ui/src/models/models';

import { QuestionProps } from './molecules/Question';

const SURVEY_STRINGS = appStrings.modules.survey;

const checkIfQuestionShouldSkip = (
  user_responses: CareerUserResponseAnswer[],
  nextQuestion: CareerQuestion,
) => {
  if (!nextQuestion.conditional_logic || nextQuestion.conditional_logic.length === 0) {
    return false;
  }

  // Check for 'SHOW' action: skip only if all conditions fail
  const showLogicFails = nextQuestion.conditional_logic.every((logic) => {
    const { logic_question_id, career_question_option_id, action } = logic;

    // If action is not 'SHOW', we don't consider it here since we only care about HIDE
    if (action !== 'SHOW') {
      return false;
    }

    const responseForCondition = user_responses.some(
      (response) =>
        response.career_question_id === logic_question_id &&
        response.career_question_option_ids.includes(career_question_option_id),
    );

    // If we find a condition where the response exists, don't skip
    return !responseForCondition;
  });

  // Check for 'HIDE' action: skip as soon as any condition is met
  const hideLogicMatches = nextQuestion.conditional_logic.some((logic) => {
    const { logic_question_id, career_question_option_id, action } = logic;

    // If action is not 'HIDE', we don't consider it here
    if (action !== 'HIDE') {
      return false;
    }

    const responseForCondition = user_responses.some(
      (response) =>
        response.career_question_id === logic_question_id &&
        response.career_question_option_ids.includes(career_question_option_id),
    );

    // If we find a condition that matches, we should skip
    return responseForCondition;
  });

  // If all SHOW conditions fail or any HIDE condition matches, we should skip
  return showLogicFails || hideLogicMatches;
};

export const getNextQuestionIndex = ({
  index,
  direction = 'FORWARD',
  userResponseSet,
  addUserQuestionResponse,
  careerQuestions,
}: {
  index: number;
  direction: 'FORWARD' | 'BACKWARD';
  userResponseSet: CareerUserResponseSet;
  addUserQuestionResponse: (response: CareerUserResponseAnswer) => void;
  careerQuestions: CareerQuestion[];
}): number => {
  const nextIndex = direction === 'FORWARD' ? index + 1 : index - 1;

  if (nextIndex < 0 || nextIndex >= careerQuestions.length) {
    return index; // if we are at the end or the beginning, just return index
  }

  const nextQuestion = careerQuestions[nextIndex];

  const { user_responses } = userResponseSet;

  const shouldSkip = checkIfQuestionShouldSkip(user_responses, nextQuestion);

  if (shouldSkip) {
    // if we're skipping a question, we should also clear out any potential response the user had
    const responseToClear = user_responses.find(
      (response) => response.career_question_id === nextQuestion.id,
    );

    if (responseToClear) {
      addUserQuestionResponse({
        id: responseToClear.id,
        career_user_response_set_id: userResponseSet.id,
        career_question_id: nextQuestion.id,
        career_question_option_ids: [], // clear out the option IDs for the response
      });
    }

    // now go to the next question and run the checks again
    return getNextQuestionIndex({
      index: nextIndex,
      direction,
      userResponseSet,
      addUserQuestionResponse,
      careerQuestions,
    });
  }

  return nextIndex;
};

export const schoolLevelQuestionProps = ({
  userResponseSet,
  careerQuestions,
  currentSchoolLevel,
  handleSelectSchoolLevel,
}: {
  userResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
  currentSchoolLevel: SchoolLevel | undefined;
  handleSelectSchoolLevel: (schoolLevel: SchoolLevel) => void;
}): QuestionProps => {
  // we just fake IDs here to get the referencing to work - IDs are required for the logic but don't matter in this case
  const questionID = 10000;
  const highSchoolOptionID = 10001;
  const collegeOptionID = 10002;
  const tradeId = 10003;
  const noCurrentEnrollmentId = 10004;

  const schoolLevelMap = new Map([
    [highSchoolOptionID, SCHOOL_LEVEL.HIGH_SCHOOL],
    [collegeOptionID, SCHOOL_LEVEL.COLLEGE],
    [tradeId, SCHOOL_LEVEL.TRADE],
    [noCurrentEnrollmentId, SCHOOL_LEVEL.NO_CURRENT_ENROLLMENT],
  ]);

  /** makes the school level options to be displayed in the question */
  function makeOptions() {
    const optionArray = Array.from(schoolLevelMap).map(([optionID, schoolLevel], index) => ({
      id: optionID,
      calculator_key: schoolLevel,
      career_question_id: 0,
      answer: SURVEY_STRINGS.highSchoolCollegeQuestion.options[schoolLevel],
      sequence: index + 1,
    }));

    return optionArray;
  }

  return {
    question: {
      id: questionID,
      title: SURVEY_STRINGS.highSchoolCollegeQuestion.title,
      question: SURVEY_STRINGS.highSchoolCollegeQuestion.question,
      calculator_key: 'school_level', // doesn't matter for this question
      sequence: 0, // used for index and progress bar
      school_level: 'both', // doesn't matter for this question
      options: makeOptions(),
    },
    totalLengthOfSurvey: careerQuestions.length,
    userResponseSet,
    responsesForQuestion: !currentSchoolLevel
      ? undefined // no response if we haven't selected a school level yet
      : {
          id: `${questionID}`,
          career_user_response_set_id: userResponseSet.id,
          career_question_id: questionID,
          career_question_option_ids: [
            // find the response in the map that matches the current school level so we can display it as selected
            Array.from(schoolLevelMap).find(([, value]) => value === currentSchoolLevel)?.[0],
          ].filter((id) => id !== undefined),
        },
    addUserQuestionResponse: (response) => {
      // we don't update the responses in the response set, but instead update the response set's school level
      const selectedSchoolLevel = Array.from(schoolLevelMap).find(([optionID]) =>
        response.career_question_option_ids.includes(optionID),
      );

      if (selectedSchoolLevel) {
        // we only care about the value and not the key in this case
        const schoolLevel = selectedSchoolLevel[1];

        handleSelectSchoolLevel(schoolLevel);
      }
    },
  };
};

type DetermineIfNextIsDisabledProps = Readonly<{
  isOnSchoolLevelQuestion: boolean;
  currentSchoolLevel: SchoolLevel | undefined;
  currentQuestionIndex: number;
  careerQuestions: CareerQuestion[];
  userResponseSet: CareerUserResponseSet;
}>;

export function determineIfNextIsDisabled({
  isOnSchoolLevelQuestion,
  currentSchoolLevel,
  currentQuestionIndex,
  careerQuestions,
  userResponseSet,
}: DetermineIfNextIsDisabledProps) {
  if (isOnSchoolLevelQuestion) {
    return !currentSchoolLevel;
  }

  const currentQuestionId = careerQuestions[currentQuestionIndex]?.id;

  const responsesForCurrentQuestion = userResponseSet.user_responses.find(
    (response) => response.career_question_id === currentQuestionId,
  );

  const isNotCustomResponse = !careerQuestions[currentQuestionIndex]?.is_custom_response;

  const hasNoResponse =
    !responsesForCurrentQuestion ||
    responsesForCurrentQuestion?.career_question_option_ids.length === 0;

  return isNotCustomResponse && hasNoResponse;
}
